/**
 * Quiz Parser - ported from reference_legacy/quebon-lms-web/lib/utils/quiz-parser.tsx
 * Handles quiz text formatting, KaTeX rendering, and image/blank parsing
 */

import { useEffect, useState, ReactNode, memo } from 'react'
import type { QuizMediaObject } from '../types/legacyQuiz'
import { KaTeX } from '../components/quiz-types/shared/katex-text'
import BlankBox from '../components/quiz-types/shared/blank-box'
import { useAdditionalImageScale } from '../components/quiz-types/print/print-common'

/**
 * Image source type that can be either a URL string or a QuizMediaObject
 */
export type ImageSource = string | QuizMediaObject

/**
 * Extracts URL from an ImageSource (string or QuizMediaObject)
 * Supports both `signedUrl` (preferred) and legacy `url` field
 */
function getImageUrl(source: ImageSource | null | undefined): string | null {
  if (!source) return null
  if (typeof source === 'string') return source
  // Prefer signedUrl, fallback to legacy url field
  return source.signedUrl ?? source.url ?? null
}

/**
 * 문제 텍스트에서 빈칸 개수 추출
 */
export function countBlanks(problem: string): number {
  const reg = /\{\{([0-9]+)?}}/gim
  let count = 0
  while (reg.exec(problem) !== null) {
    count++
  }
  return count
}

/**
 * 모든 빈칸이 채워졌는지 확인
 */
export function isAllBlanksFilled(answers: string[]): boolean {
  return answers.every(answer => answer !== null && answer !== undefined && answer !== '')
}

/* ----------------------------------------------------------------------------------
 * Formatter implementation from formatter.tsx
 * ---------------------------------------------------------------------------------- */

const replaceCRLF = (t: string, id: string | null, imgs?: ImageSource[]) => {
  const _id = id != null ? id : 'tmp'

  const imageReg = /({image=\d+(?:,\s*[^}]+)*})/
  const videoReg = /({video=https:\/\/www\.youtube\.com\/watch\?[^}]+})/

  // First remove video tags
  const withoutVideos = t.replace(videoReg, '')

  const parts = escape(withoutVideos)
    .split(/(\n)/)
    .flatMap((part) => part.split(imageReg).filter(Boolean))

  return parts.map((item, idx) => {
    if (item.indexOf('{image') !== -1 && imgs) {
      return <ImageWrap key={`image-${idx}`} text={item} imgSources={imgs} />
    } else {
      return (
        <span key={_id + '_other_' + idx}>{item === '\n' ? <br /> : item}</span>
      )
    }
  })
}

const ImageWrap = ({ text, imgSources }: { text: string; imgSources: ImageSource[] }) => {
  const startIndex = text.indexOf('{image')
  const endIndex = text.indexOf('}')

  const rawString = text.substring(startIndex, endIndex + 1)
  const sliceString = rawString.replace(/{/g, '').replace(/}/g, '').split(',')
  let sizeString: string | null = null
  let numberString: string | null = null
  let spaceXString: string | null = null
  let inlineString = false
  const imageIndex = parseInt(sliceString[0].split('=')[1], 10)
  for (let i = 1; i < sliceString.length; i++) {
    if (sliceString[i].indexOf('scale=') !== -1) {
      sizeString = sliceString[i].replace(/scale=/, '')
    }
    if (sliceString[i].indexOf('number=') !== -1) {
      numberString = sliceString[i].replace(/number=/, '')
    }
    if (sliceString[i].indexOf('spaceX=') !== -1) {
      spaceXString = sliceString[i].replace(/spaceX=/, '')
    }
    if (
      sliceString[i].indexOf('inline') !== -1 &&
      sliceString[i].split('=')[1] === 'true'
    ) {
      inlineString = true
    }
  }

  // Extract URL from either string or QuizMediaObject
  const source = imgSources[imageIndex]
  const url = getImageUrl(source)

  if (!url) return null

  return (
    <ExternalImage
      imgSrc={url}
      imageIndex={imageIndex}
      scale={sizeString ? Number(sizeString) : 1}
      number={numberString ? Number(numberString) : null}
      spaceX={spaceXString ? Number(spaceXString) : null}
      inline={inlineString}
    />
  )
}

function ExternalImage({
  imgSrc,
  imageIndex,
  scale,
  number,
  spaceX,
  inline,
}: {
  imgSrc: string
  imageIndex: number
  scale: number
  number?: number | null
  spaceX?: number | null
  inline?: boolean
}) {
  const multiplier = useAdditionalImageScale(imageIndex)
  const [img, setImg] = useState<HTMLImageElement | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let isMounted = true
    const _img = new Image()
    _img.onload = () => {
      if (isMounted) {
        setImg(_img)
        setLoaded(true)
      }
    }
    _img.onerror = () => {
      // Handle error silently
    }
    _img.src = imgSrc

    return () => {
      isMounted = false
    }
  }, [imgSrc])

  const finalScale = scale * multiplier

  const style: React.CSSProperties = {
    marginTop: 16,
    marginBottom: 16,
    display: inline ? 'inline-flex' : 'flex',
    justifyContent: 'center',
    ...(spaceX ? { marginLeft: spaceX, marginRight: spaceX } : {}),
  }

  return (
    <div style={style}>
      {number !== null && <div style={{ marginRight: 8 }}>({number})</div>}
      {loaded && img && (
        <img src={img.src} width={img.width * finalScale + 'px'} alt="quiz img" />
      )}
    </div>
  )
}

const escape = (t: string) => {
  return t
    .replace(/＝/g, '=') // full-width equal sign (＝) → regular equal sign (=)
    .replace(/－/g, '-') // 기존: full-width hyphen-minus (－) → regular hyphen (-)
    // .replace(/－/g, '–') // full-width hyphen-minus (－) → en dash (–)
    // .replace(/-/g, '–')  // regular hyphen (-) → en dash (–)
    .replace(/＋/g, '+') // full-width plus sign (＋) → regular plus sign (+)
    .replace(/𝑚/g, 'm') // Greek small letter m (𝑚) → regular small letter m (m)
    .replace(/&lt;/g, '<') // HTML less-than sign (&lt;) → regular less-than sign (<)
    .replace(/&gt;/g, '>') // HTML greater-than sign (&gt;) → regular greater-than sign (>)
    .replace(/&quot;/g, '"') // HTML quotation mark (&quot;) → regular quotation mark (")
}

export function formatText(text: string, id: string | null, imgs?: ImageSource[]): ReactNode {
  const _id = id != null ? id : 'tmp'

  if (text == null) {
    return ''
  }

  const _t = text.normalize ? text.normalize('NFC') : text
  let r
  const katexIndices: number[] = []
  let lastIndex = 0

  const katexReg = new RegExp(/\$\$([^$]+)\$\$/gim)

  while ((r = katexReg.exec(_t))) {
    if (r.index < lastIndex) {
      continue
    }

    katexIndices.push(r.index, katexReg.lastIndex)
    lastIndex = katexReg.lastIndex
  }
  katexIndices.push(_t.length)

  let lastIdx = 0
  if (katexIndices.length > 0) {
    return katexIndices.map((i, seq) => {
      const pre = _t.substring(lastIdx, i)
      lastIdx = i

      const key = _id + '_kat_' + seq

      const match = katexReg.exec(escape(pre))

      if (match != null && match.index >= 0) {
        return <KaTeX key={key} math={match[1]} />
      } else {
        return (
          <span className="formatText_nonKatex" key={key}>
            {replaceCRLF(pre, id, imgs)}
          </span>
        )
      }
    })
  } else {
    return <span className="formateText">{replaceCRLF(_t, id, imgs)}</span>
  }
}

// []{{ * }} -> {{*}}
export function replaceSquareBracketPattern(str: string) {
  const regex = /\[\](\{\{.*?\}\})/g
  return str.replace(regex, '$1')
}

export type ProducerFunction = (index: number, content: string) => ReactNode

/**
 * Memoized component for rendering static text chunks in formatProblem.
 * This prevents re-parsing KaTeX and re-rendering static text when
 * only blank values or UI state changes (dropdown, selection, etc.)
 */
interface ProblemTextChunkProps {
  text: string
  id: string | null
  imgs?: ImageSource[]
  className?: string
}

const ProblemTextChunk = memo(function ProblemTextChunk({
  text,
  id,
  imgs,
  className = 'formatProblem',
}: ProblemTextChunkProps) {
  return (
    <span className={className}>
      {formatText(text, id, imgs)}
    </span>
  )
})

export function formatProblem(
  problem: string,
  id: string | null,
  producer: ProducerFunction,
  imgs?: ImageSource[]
): ReactNode[] {
  const _id = id != null ? id : 'tmp'

  let _p = problem.normalize ? problem.normalize('NFC') : problem
  _p = replaceSquareBracketPattern(_p)
  const totalReg = /(({\{(.*?)}})|(<\/?frac>)|(\/\/)|(<box>{.*?}<\/box>))/gim
  const answerReg = /{\{(.*?)}}/gim

  let r
  const answerIndices: number[] = []

  const parser = new RegExp(totalReg)
  let lastIdx = -1
  while ((r = parser.exec(_p))) {
    if (r.index === lastIdx) {
      answerIndices.push(parser.lastIndex)
    } else {
      answerIndices.push(r.index, parser.lastIndex)
    }
    lastIdx = parser.lastIndex
  }
  answerIndices.push(_p.length)

  lastIdx = 0

  let answerIdx = 0

  if (answerIndices.length > 0) {
    let frac = false
    let fracStartIdx = -1

    const fracs: [number, number][] = []

    const reg = new RegExp(answerReg)

    let skip = 0

    const result: ReactNode[] = answerIndices
      .map((i, seq) => {
        const pre = _p.substring(lastIdx, i)

        lastIdx = i

        const key = _id + '_prob_' + seq

        const _seq = seq - skip

        const match = reg.exec(pre)

        if (match != null && match.index >= 0) {
          const a = producer(answerIdx, match[1])
          answerIdx++
          return a
        } else {
          const t = pre.trim()

          if (t === '') {
            skip++
            return null
          }

          if (t === '<frac>') {
            frac = true
            fracStartIdx = _seq
            return '<frac>'
          } else if (t === '//') {
            return '<sep>'
          } else if (t === '</frac>') {
            if (frac) {
              frac = false
              fracs.push([Number(fracStartIdx), Number(_seq)])
              return '</frac>'
            }
          }

          // check if it's a box tag
          const boxReg = /<box>\{(.*?)\}<\/box>/g
          if (boxReg.test(pre)) {
            const boxContent = pre.replace(boxReg, '$1')
            return (
              <BlankBox key={key + 'blank_box'}>
                {boxContent}
              </BlankBox>
            )
          }

          // Use memoized ProblemTextChunk for static text to prevent re-parsing
          return (
            <ProblemTextChunk
              key={key}
              text={pre}
              id={id}
              imgs={imgs}
              className="formatProblem"
            />
          )
        }
      })
      .filter((o): o is ReactNode => o != null)

    for (let i = 0; i < fracs.length; i++) {
      const fracItem = fracs[i]

      const startIdx = fracItem[0]
      const endIdx = fracItem[1]

      const numerator: ReactNode[] = []
      const denominator: ReactNode[] = []

      let sep = false

      for (let j = startIdx + 1; j < endIdx; j++) {
        if (result[j] === '<sep>') {
          sep = true
          continue
        }

        if (result[j] === '' || result[j] === undefined) {
          continue
        } else if (result[j] === '<frac>') {
          continue
        } else if (result[j] === '</frac>') {
          sep = false
          continue
        }
        if (!sep) {
          numerator.push(result[j])
        } else {
          denominator.push(result[j])
        }
      }

      const component = (
        <div key={_id + i} className="problem-frac-wrap">
          <div className="problem-frac-numerator">{numerator}</div>
          <div className="problem-frac-denominator">{denominator}</div>
        </div>
      )

      const len = endIdx - startIdx + 1

      result.splice(startIdx, len, component)

      for (let j = i + 1; j < fracs.length; j++) {
        fracs[j][0] = fracs[j][0] - len + 1
        fracs[j][1] = fracs[j][1] - len + 1
      }
    }

    return result
  } else {
    // Use memoized ProblemTextChunk for static text
    return [
      <ProblemTextChunk
        key="formatProblem_other"
        text={_p}
        id={id}
        imgs={imgs}
        className="formatProblem_other"
      />
    ]
  }
}
